import React, { useEffect, useState } from "react"
import { Checklist } from "../model/ChecklistDtos"
import { Button } from "devextreme-react"
import { t } from "i18next"
import { ClickEvent } from "devextreme/ui/button"
import ChecklistsApi from "../api/ChecklistApi"
import ScheduleForm, { FormState } from "./ScheduleForm"
import { useChecklistScheduleMapper } from "../hook/useChecklistScheduleMapper"
import { useScheduleValidation } from "../hook/useChecklistScheduleValidator"
import { IconButtonDelete, IconButtonFile, Modal, Row } from "../../../core/components"
import { useNavigate } from "react-router-dom"

const defaultFormState: FormState = {
    idChecklist: undefined,
    onceDate: undefined,
    startDate: undefined,
    endDate: undefined,
    enableRepeat: false,
    repeatType: 1,
    repeatOnDayDaily: [],
    repeatOnDayWeekly: 0,
    repeatOnMonths: [],
    repeatOnWeekdayOnMonths: false,
    monthlyFrequencyDay: [],
    monthlyOnWeekDays: [],
    monthlyFrequencyOnWeekDay: [],
}

interface AppointmentFormModalProps {
    isOpen: boolean
    checklists: Checklist[]
    initialData: Partial<FormState>
    defaultDate?: Date
    onClose: () => void
    onSubmit: (data: FormState) => void
    onDelete: (idChecklistSchedule: number) => void
}

export const ScheduleFormModal: React.FC<AppointmentFormModalProps> = ({
    isOpen,
    checklists,
    initialData,
    onSubmit,
    onClose,
    onDelete
}) => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [formState, setFormState] = useState<FormState>({
        ...defaultFormState,
        onceDate: initialData.startDate ?? undefined,
        startDate: initialData.startDate ?? undefined
    })
    const { validate, validationErrors, clearValidation } = useScheduleValidation(formState)
    const { toDto } = useChecklistScheduleMapper()

    useEffect(() => {
        let startDate: string | undefined = undefined
        let idChecklistSchedule: number | undefined = undefined
        let idChecklist: number | undefined = undefined

        if (initialData.startDate) startDate = initialData.startDate
        if (initialData.idChecklistSchedule) idChecklistSchedule = initialData.idChecklistSchedule
        if (initialData.idChecklist) idChecklist = initialData.idChecklist

        setFormState(prev => ({
            ...prev,
            ...initialData
        }))

        if (idChecklist && idChecklistSchedule) {
            fetchSchedule(idChecklist, idChecklistSchedule)
        } else {
            setFormState(defaultFormState)
        }
    }, [initialData])

    async function fetchSchedule(idChecklist: number, idChecklistSchedule: number) {
        setLoading(true)
        const schedule = await ChecklistsApi.getSchedule(idChecklist, idChecklistSchedule)

        if (!schedule) return

        setFormState(prev => ({
            ...prev,
            ...toDto(schedule)
        }))

        setLoading(false)
    }

    const handleChange = (updates: Partial<FormState>) => {
        setFormState((prev) => ({ ...prev, ...updates }));
        clearValidation(Object.keys(updates) as (keyof FormState)[]);
    };

    const handleSubmit = (e: ClickEvent) => {
        if (!validate()) {
            return
        }

        onSubmit?.(formState)
        handleCloseModal()
    }

    const handleDelete = () => {
        if (!formState.idChecklistSchedule) return
        onDelete(formState.idChecklistSchedule!)
        handleCloseModal()
    }


    const handleCloseModal = () => {
        clearValidation()
        onClose()
    }

    const handleChecklistNavigation = () => {
        if (!formState.idChecklist) {
            return
        }

        navigate(`/checklists/${formState.idChecklist}`)
    }

    return (
        <Modal isOpen={isOpen} onClose={handleCloseModal} loading={loading} useWidth>
            <Modal.Content>
                <Row alignItems="flex-start">
                    <h2 style={{ marginTop: 0 }}>Schedule Checklist</h2>
                    <Row.End>
                    {formState.idChecklist && <IconButtonFile tooltip={t("Show Checklist")} onClick={handleChecklistNavigation} />}
                    {formState.idChecklistSchedule && <IconButtonDelete onClick={handleDelete} /> }
                    </Row.End>
                </Row>

                <ScheduleForm
                    loading={loading}
                    checklists={checklists}
                    initialData={formState}
                    validationErrors={validationErrors}
                    handleChange={handleChange}
                    usageContext="calendar"
                    resetValidationErrors={() => clearValidation()} />
            </Modal.Content>

            <Modal.Footer>
                <Button
                    className="border-radius-small"
                    type="success"
                    text={t("Save")}
                    width={100}
                    disabled={loading}
                    onClick={handleSubmit} />
                <Button
                    hoverStateEnabled
                    className="border-radius-small"
                    text={t("Close")}
                    width={100}
                    disabled={loading}
                    onClick={handleCloseModal} />
            </Modal.Footer>
        </Modal>
    )
}
